import type { Currency, JobFee } from "./api";

const symbol: Record<Currency, string> = { GBP: "£", EUR: "€", USD: "$" };

/** "£80k–£95k", "from £80k", "up to £95k", or "" when unknown. */
export function formatSalary(min: number | null, max: number | null, currency: Currency): string {
  const s = symbol[currency];
  const k = (n: number) => (n >= 1000 && n % 1000 === 0 ? `${s}${n / 1000}k` : `${s}${n.toLocaleString("en-GB")}`);
  if (min != null && max != null) return min === max ? k(min) : `${k(min)}–${k(max)}`;
  if (min != null) return `from ${k(min)}`;
  if (max != null) return `up to ${k(max)}`;
  return "";
}

export function formatFee(fee: JobFee, currency: Currency): string {
  return fee.type === "percent"
    ? `${fee.percent}%`
    : new Intl.NumberFormat("en-GB", { style: "currency", currency, maximumFractionDigits: 2 }).format(fee.amount);
}

export const JOB_STATUS_LABEL = { open: "Open", on_hold: "On hold", filled: "Filled", lost: "Lost" } as const;

// Calendar days (start dates) are formatted in UTC so the viewer's timezone can't move them.
const dayFmt = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });

/** "2026-10-10" -> "10 Oct 2026". */
export function formatDay(isoDate: string): string {
  return dayFmt.format(new Date(`${isoDate}T00:00:00Z`));
}

// Skills are stored lower-case so searches match; these are shown with their usual capitals.
const SKILL_NAMES: Record<string, string> = {
  aws: "AWS", sql: "SQL", gcp: "GCP", css: "CSS", html: "HTML", api: "API", apis: "APIs", ".net": ".NET",
  "c#": "C#", "c++": "C++", "f#": "F#", php: "PHP", hl7: "HL7", fhir: "FHIR", dbt: "dbt", ios: "iOS",
  "node.js": "Node.js", javascript: "JavaScript", typescript: "TypeScript", graphql: "GraphQL",
  postgresql: "PostgreSQL", mysql: "MySQL", devops: "DevOps", ui: "UI", ux: "UX", ai: "AI", ml: "ML", seo: "SEO",
  "sql server": "SQL Server", "power bi": "Power BI", bi: "BI", mongodb: "MongoDB", dynamodb: "DynamoDB",
  bigquery: "BigQuery", "github actions": "GitHub Actions", gitlab: "GitLab", "ci/cd": "CI/CD", nlp: "NLP", llm: "LLM",
  mlops: "MLOps", pytorch: "PyTorch", tensorflow: "TensorFlow", sre: "SRE", iam: "IAM", siem: "SIEM", tdd: "TDD",
  bdd: "BDD", etl: "ETL", sap: "SAP", saas: "SaaS", fintech: "Fintech", hubspot: "HubSpot", servicenow: "ServiceNow",
  "next.js": "Next.js", "asp.net": "ASP.NET", "objective-c": "Objective-C", vba: "VBA", cobol: "COBOL",
  "dynamics 365": "Dynamics 365", grpc: "gRPC", rest: "REST", figma: "Figma", r: "R", c: "C",
};

/** "aws" -> "AWS", "distributed systems" -> "Distributed systems", "kubernetes" -> "Kubernetes". */
export function displaySkill(skill: string): string {
  const known = SKILL_NAMES[skill];
  if (known) return known;
  return skill
    .split(" ")
    .map((word, i) => SKILL_NAMES[word] ?? (i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");
}
